import React, {useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import useSound from 'use-sound';
import { camelizeKeys } from 'humps';
import { usePageVisibility } from 'react-page-visibility';
import usePlayerReducer from '../Reducers/Player';
import GetAlignStyles from './AlignStyles';
import { useDebouncedCallback } from 'use-debounce';

const VolumeMute = ({
  fill = '#000', fillOpacity = '1', size = 50
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill={fill}
    fillOpacity={fillOpacity}
    width={size + 'px'}
    height={size + 'px'}>
      <path d="M0 0h24v24H0z" fill="none"/>
      <path d="M7 9v6h4l5 5V4l-5 5H7z"/>
  </svg>
);

const VolumeUp = ({
  fill = '#000', fillOpacity = '1', size = 50
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill={fill}
    fillOpacity={fillOpacity}
    width={size + 'px'}
    height={size + 'px'}>
      <path d="M0 0h24v24H0z" fill="none"/>
      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
  </svg>
);

const usePrevious = (value) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const usePlayerSetting = (defaultSetting = {}) => {
  const [setting, setSetting] = useState(defaultSetting);
  useEffect(() => {
    fetch(
    `${process.env.APP_URL}/player/1?domain=${window.Shopify.shop}`)
    .then(res => res.json())
    .then(data => {
      setSetting(camelizeKeys(data));
    });
  }, [setSetting]);
  return [setting];
};

const styles = {
  container: {
    cursor: 'pointer',
    display: 'inline-block'
  }
}

const defaultState = {
  true: {
    soundSeek: 0,
    userState: 'PAUSED',
  },
  false: {
    soundSeek: 0,
    userState: '',
  }
};


export const PlayerCore = ({setting, isPreview = false}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoPlayed, setAutoPlayed] = useState(false);
  const [{
    soundSeek, userState
  }, dispatch] = usePlayerReducer(
    setting.localStorageKey, defaultState[isPreview]
  );

  const isVisible = usePageVisibility();
  const prevIsVisible = usePrevious(isVisible);

  const [play, {
    stop, pause, duration, sound
  }] = useSound(setting.musicData, {
    autoplay: false,
    volume: setting.volume / 10,
    loop: setting.loop,
    // interrupt: true,
  });

  const debouncedPlay = useDebouncedCallback(play, 5);

  const handlePause = useCallback(() => {
    pause();
    if (setting.syncMusic) {
      dispatch({
        type: 'SET_SOUND_SEEK', soundSeek: sound?.seek()
      });
    }
  }, [pause, dispatch, setting.syncMusic]);

  // hack for isPlaying - because useSound hook return isPlaying false
  // when autoPlay is enabled.
  useEffect(() => {
    sound?.on('play', (id) => {
      setIsPlaying(true);
    });
    sound?.on('stop', (id) => {
      setIsPlaying(false);
      dispatch({
        type: 'SET_SOUND_SEEK', soundSeek: 0
      });
    });
    sound?.on('end', (id) => {
      if (!setting.loop) {
        setIsPlaying(false);
        handlePause()
      }
      // dispatch({
      //   type: 'SET_SOUND_SEEK', soundSeek: 0
      // });
    });
    sound?.on('pause', (id) => {
      setIsPlaying(false);
    });
  }, [sound, setIsPlaying, dispatch, handlePause]);

  // Set seek at proper position
  useEffect(() => {
    sound?.seek(soundSeek);
  }, [sound, soundSeek]);

  // Handle window unload evnet.
  useEffect(() => {
    window.onbeforeunload = () => {
      handlePause();
    };
    return () => {
      window.onbeforeunload = null;
    }
  }, [window, handlePause]);

  // Handle auto play for the first load.
  useEffect(() => {
    if (!sound) return;
    if (!isPlaying && userState !== 'PAUSED' &&
        isVisible && setting.autoPlay && !autoPlayed) {
      setAutoPlayed(true)
      debouncedPlay();
      // console.log('first load...............')
    }
  }, [play, sound, isVisible, userState, setting.autoPlay, isPlaying]);

  // Handle visibility = visible (while tab switch)
  useEffect(() => {
    if (!sound) return;
    if (!isPlaying && userState !== 'PAUSED' &&
        !prevIsVisible && isVisible) {
        // only allow one sound instance if possible
        // console.log('while tab switch...............')
        debouncedPlay();
    }
  }, [play, sound, prevIsVisible, isVisible, userState, isPlaying]);

  // Handle visibility = not visible (while tab switch)
  useEffect(() => {
    if (!sound) return;
    if (!isVisible && isPlaying) {
      handlePause();
    }
  }, [sound, isVisible, isPlaying, pause]);

  // cleanup on unmount.
  useEffect(() => {
    return () => {
      stop();
    }
  }, [stop]);

  // Handle user clicked toogle.
  const toggle = useCallback(() => {
    if (isPlaying) {
      handlePause();
      dispatch({ type: 'SET_USER_STATE', userState: 'PAUSED' });
    } else {
      debouncedPlay();
      dispatch({ type: 'SET_USER_STATE', userState: 'PLAYED' });
    }
  }, [isPlaying, play, pause, dispatch]);

  return (
    <div style={GetAlignStyles(setting, isPreview)}>
      <div onClick={toggle} style={styles.container}>
        { isPlaying
          ? <VolumeUp
              fill={setting.controlColor}
              fillOpacity={setting.opacity}
              size={setting.iconSize} />
          : <VolumeMute
              fill={setting.controlColor}
              fillOpacity={setting.opacity}
              size={setting.iconSize} />
        }
      </div>
    </div>
  )
};

const bannerStyles = {
  overlay: {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: 50,
    backgroundColor: 'rgba(0,0,0,0.999)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    textAlign: 'center',
    zIndex: 999,
    borderRadius: 5
  },
  action: {
    backgroundColor: '#fff',
    color: '#333',
    cursor: 'pointer',
    borderRadius: 3,
    padding: 12,
    paddingRight: 22,
    paddingLeft: 22,
    fontWeight: 'bold',
    fontSize: 22,
    display: 'inline-block',
    marginBottom: 20
  },
  title: {
    fontWeight: 'bold',
    fontSize: 28,
    marginBottom: 15,
    color: '#fff'
  },
  tagline: {
    fontWeight: 500,
    fontSize: 19,
    marginBottom: 30,
    color: '#fff'
  },
  link: {
    color: '#fff',
    textDecoration: 'underline'
  },
};

export const Banner = () => {
  let params = new URLSearchParams(document.location.search.substring(1));
  const [isTesting, setIsTesting] = useState(params.get('testing-musica-activation'));
  const [okay, setOkay] = useState(false)
  const [yes, setYes] = useState(false)
  const [no, setNo] = useState(false)

  const onOkay = () => {
    setOkay(true)
    // dispatch({ type: 'SET_USER_STATE', userState: '' });
  }

  const onYes = () => {
    setYes(true)
  }

  const onNo = () => {
    setNo(true)
  }

  const onReview = () => {
    window.open(`https://apps.shopify.com/play-custom-music-in-store-background#modal-show=ReviewListingModal`)
    window.top.close();
  }

  const onChat = () => {
    window.open(`${params.get('crisp-chat-link')}&user_email=${params.get('user_email')}&user_nickname=${params.get('user_nickname')}&token_id=${params.get('token_id')}`)
    window.top.close();
  }

  const onNoThanks = () => {
    setIsTesting(false)
  }

  if (isTesting) {
    return (
      <div style={bannerStyles.overlay}>
        <div style={bannerStyles.title}>Testing Musica Activation</div>
          <p style={bannerStyles.tagline}>
            { okay
              ? 'Did you hear the music you configured?'
              : 'you will see music icon in the configured corner.'
            }
          </p>
          {
            !okay && <span style={{...bannerStyles.action, backgroundColor: '#38dfb4', color: '#fff'}} onClick={() => onOkay()}>OK</span>
          }
          {
            okay && !yes && !no && (
              <div style={{display: 'flex', flexDirection: 'row'}}>
                <span style={bannerStyles.action} onClick={() => onNo()}>No</span>
                <span
                  style={{...bannerStyles.action, marginLeft: 20, backgroundColor: '#38dfb4', color: '#fff'}}
                  onClick={() => onYes()}>Yes</span>
              </div>
            )
          }
          {
            yes && (
              <div style={{display: 'flex', flexDirection: 'row'}}>
                <span
                    style={{...bannerStyles.action, marginLeft: 20}}
                    onClick={() => onNoThanks()}>No Thanks</span>
                <span
                    style={{...bannerStyles.action, marginLeft: 20, backgroundColor: '#38dfb4', color: '#fff'}}
                    onClick={() => onReview()}>Review Us</span>
              </div>
            )
          }
          {
            no && (
              <span
                  style={{...bannerStyles.action, marginLeft: 20, backgroundColor: '#38dfb4', color: '#fff'}}
                  onClick={() => onChat()}>Chat with Us</span>
            )
          }
      </div>
    );
  }
  return null
};

const Player = () => {
  const [setting] = usePlayerSetting({});
  const [showBanner, setShowBanner] = useState(false)
  if (setting?.status) {
    if (setting.location === 'home' &&
        (window.location.pathname !== '/' && window.location.pathname !== '/password')) {
      return null;
    }
    return (
      <>
        <Banner />
        <PlayerCore setting={setting} />
      </>
    )
  }
  return null;
};

export default Player;
